import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  Package,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  BookOpen,
  Stethoscope,
  RefreshCw,
  Sunrise,
  Footprints,
  UserCheck,
  Droplets,
  Ban,
  Hourglass,
  Wrench,
  PhoneCall,
} from 'lucide-react';
import { Card, CardBody, Skeleton, Heading, Eyebrow, cn } from '@insolech/ui';
import { patientPublicQueries, type PatientPublicView } from '@insolech/sdk';
import { PatientLayout } from '@/components/layout/PatientLayout';
import { patientProductName } from './productDisplay';

/**
 * PatientPublicPage: the page a patient sees when they scan their tag.
 *
 *   /p/:token         (full HMAC-signed bearer token; encoded in QR image)
 *   /q/:shortCode     (human-typeable backup form, e.g. INS-7K2X9)
 *
 * Layout (August 2026 spec, rearranged 2026-09-16 because the eight-block
 * usage text left the half-width prescriber box mostly empty):
 *   greeting with the patient's first name, unchanged
 *   order status timeline (kept from the previous page)
 *   REORDERS, first box, full width, ONLY when the clinic has it on
 *   prescriber | product material   (side by side from `sm`)
 *   usage instructions, full width
 *
 * Mobile first: `sm` is 480px in this repo, so below that the one remaining
 * pair stacks (prescriber above product) and everything is one column. The
 * usage blocks run two columns from `sm` so their line length stays close to
 * the half-column they came from; on a phone they are one column, exactly
 * the width they already had. The producer box was removed: a page on
 * INSOLECH's own site does not need to announce who made the product.
 *
 * Reorders per clinic (migration 20270358): `view.reorders_enabled` comes
 * from the clinic that MADE the insoles. Off means the box is not rendered at
 * all, and the server refuses a submission regardless of what this page does
 * (BEFORE INSERT trigger on patient_reorders + an early 403 in
 * submit-reorder). Hiding here is UX; the refusal is the enforcement.
 *
 * PII rules (Phase 1 §08 §17), with one deliberate change: the podiatrist's
 * DISPLAY NAME is now shown in the prescriber box at Rafael's instruction.
 * Still never: last name, diagnosis, modifications, STL, comments, or any
 * contact detail of the podiatrist. Clinic name + phone remain for
 * patient-to-clinic contact.
 */

const STATUS_STEPS = [
  { key: 'submitted', labelKey: 'status_step_submitted' },
  { key: 'in_review', labelKey: 'status_step_in_review' },
  { key: 'in_production', labelKey: 'status_step_in_production' },
  { key: 'in_delivery', labelKey: 'status_step_in_delivery' },
  { key: 'delivered', labelKey: 'status_step_delivered' },
] as const;

const STATUS_INDEX: Record<string, number> = {
  draft: 0,
  submitted: 0,
  in_review: 1,
  in_production: 2,
  in_delivery: 3,
  delivered: 4,
  cancelled: -1,
  returned: -1,
  on_hold: -1,
};

/**
 * USAGE INSTRUCTIONS: fixed content, the same for every product and every
 * patient (Rafael, 2026-09-16). Eight blocks, each an icon from the app's
 * own lucide set beside its heading. The text lives in the `patient` i18n
 * namespace (usage_N_title / usage_N_body), English canonical, Arabic and
 * Spanish empty until translated (they fall back to English). No per-family
 * variant, no query.
 */
const USAGE_BLOCKS = [
  { n: 1, Icon: Sunrise },
  { n: 2, Icon: Footprints },
  { n: 3, Icon: UserCheck },
  { n: 4, Icon: Droplets },
  { n: 5, Icon: Ban },
  { n: 6, Icon: Hourglass },
  { n: 7, Icon: Wrench },
  { n: 8, Icon: PhoneCall },
] as const;

type T = ReturnType<typeof useTranslation>['t'];

export function PatientPublicPage() {
  const { t } = useTranslation('patient');
  const { token, shortCode } = useParams<{ token?: string; shortCode?: string }>();

  const verify = useQuery({
    queryKey: ['patient-public', token ?? shortCode],
    queryFn: () =>
      patientPublicQueries.verifyQrToken({
        ...(token ? { token } : {}),
        ...(shortCode ? { shortCode } : {}),
      }),
    retry: false,
    enabled: !!(token || shortCode),
  });

  // Loading
  if (verify.isLoading) {
    return (
      <PatientLayout>
        <div className="space-y-4" data-testid="patient-public-loading">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </PatientLayout>
    );
  }

  // Error / not-found: generic copy, no info leak
  if (verify.isError || !verify.data) {
    return (
      <PatientLayout>
        <Card>
          <CardBody className="text-center py-10">
            <AlertCircle className="h-10 w-10 mx-auto text-error-500" aria-hidden />
            <Heading as="h1" size="h3" className="mt-4">
              {t('not_found_title', 'We couldn\'t find your order')}
            </Heading>
            <p className="mt-3 text-text-secondary">
              {t(
                'not_found_body',
                'This link may have expired, or the code may have been entered incorrectly. Please contact your clinic for help.',
              )}
            </p>
          </CardBody>
        </Card>
      </PatientLayout>
    );
  }

  const view = verify.data;
  return (
    <PatientLayout clinicName={view.clinic_name}>
      <PatientHero view={view} t={t} />
      <StatusTimeline view={view} t={t} />

      {/* Off means gone: no box, not a hidden one. The server refuses a
          submission for this clinic regardless of this branch. */}
      {view.reorders_enabled ? (
        <ReordersBox
          view={view}
          {...(token ? { token } : {})}
          {...(shortCode ? { shortCode } : {})}
          t={t}
        />
      ) : null}

      {/* The only side-by-side pair: prescriber | product material. Stacks
          below `sm` (480px), prescriber first. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-testid="patient-info-grid">
        <PrescriberBox view={view} t={t} />
        <ProductBox view={view} t={t} />
      </div>
      {/* Usage instructions, full width, last. */}
      <UsageBox t={t} />
    </PatientLayout>
  );
}

function PatientHero({ view, t }: { view: PatientPublicView; t: T }) {
  // The server resolves the first name (patient_greeting_first_name,
  // migration 20270359): the line-level name first, clinic identifiers
  // skipped, '' when no alphabetic token exists. Defence in depth here: a
  // token with a digit is never a first name, so it is never greeted.
  const raw = (view.patient_first_name ?? '').trim();
  const firstName = /\p{Nd}/u.test(raw) ? '' : raw;
  return (
    <div className="mb-6" data-testid="patient-hero">
      <Eyebrow>{t('title_your_insoles', 'Your INSOLECH insoles')}</Eyebrow>
      <Heading as="h1" size="h1" className="mt-1" data-testid="patient-greeting">
        {firstName
          ? t('greeting_hello', 'Hello, {{name}}', { name: firstName })
          : t('greeting_hello_plain', 'Hello')}
      </Heading>
      <p className="mt-2 text-text-secondary text-sm sm:text-base">
        {t(
          'lead_subtitle',
          'Here\'s the latest on your custom insoles from {{clinic}}.',
          { clinic: view.clinic_name },
        )}
      </p>
    </div>
  );
}

function StatusTimeline({ view, t }: { view: PatientPublicView; t: T }) {
  const currentIdx = STATUS_INDEX[view.order_status] ?? 0;
  const isCancelled = ['cancelled', 'returned', 'on_hold'].includes(view.order_status);
  return (
    <Card className="mb-4">
      <CardBody>
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-base font-semibold">
            {t('status_heading', 'Order status')}
          </h2>
          {view.expected_delivery_date ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
              <Clock className="h-3.5 w-3.5" />
              {t('expected_delivery', 'Expected: {{date}}', {
                date: formatDate(view.expected_delivery_date),
              })}
            </span>
          ) : null}
        </div>

        {isCancelled ? (
          <div className="rounded-md bg-warning-50 border border-warning-200 px-3 py-3 text-sm text-warning-800">
            {t(
              'status_paused',
              'Your order is currently {{status}}. Please contact your clinic for the latest update.',
              { status: view.order_status.replace('_', ' ') },
            )}
          </div>
        ) : (
          <ol className="space-y-3" data-testid="patient-status-timeline">
            {STATUS_STEPS.map((step, idx) => {
              const reached = idx <= currentIdx;
              const current = idx === currentIdx;
              return (
                <li
                  key={step.key}
                  className="flex items-start gap-3"
                  data-testid={`patient-status-${step.key}`}
                  data-state={current ? 'current' : reached ? 'done' : 'pending'}
                >
                  <span
                    className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      reached
                        ? current
                          ? 'border-brand-500 bg-brand-500 text-white'
                          : 'border-success-500 bg-success-500 text-white'
                        : 'border-border-subtle bg-surface-raised text-text-muted'
                    }`}
                    aria-hidden
                  >
                    {reached ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <span className="text-[11px] font-semibold">{idx + 1}</span>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm ${
                        current ? 'font-semibold text-text-primary' : reached ? 'text-text-primary' : 'text-text-muted'
                      }`}
                    >
                      {t(`status_step_${step.key}`, defaultStepLabel(step.key))}
                    </p>
                    {current && view.delivered_at && step.key === 'delivered' ? (
                      <p className="text-xs text-text-secondary mt-0.5">
                        {t('delivered_on', 'Delivered on {{date}}', {
                          date: formatDate(view.delivered_at),
                        })}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * One of the five content areas. A Card with a small icon + uppercase label
 * heading, then whatever the box holds. `h-full` so the two boxes of a pair
 * stretch to the same height when side by side.
 */
function InfoBox({
  label,
  icon,
  testId,
  className,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  testId: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn('h-full', className)} data-testid={testId}>
      <CardBody>
        <div className="flex items-center gap-2 mb-2">
          <span className="h-7 w-7 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center shrink-0" aria-hidden>
            {icon}
          </span>
          <p className="text-xs uppercase tracking-wider text-text-muted">{label}</p>
        </div>
        {children}
      </CardBody>
    </Card>
  );
}

/**
 * REORDERS. Rendered only when the clinic has reorders on (the caller
 * gates on view.reorders_enabled). Inside, the gating is EXACTLY what the
 * previous page did: nothing before delivery, the "opens once delivered"
 * note when delivered but not yet reorderable, otherwise the same Link to
 * the same reorder route. What changed is only where it sits: first box,
 * full width, instead of a pill stuck to the bottom of the viewport.
 */
function ReordersBox({
  view,
  token,
  shortCode,
  t,
}: {
  view: PatientPublicView;
  token?: string;
  shortCode?: string;
  t: T;
}) {
  const canReorder = view.actions.can_request_reorder;
  const isDelivered = view.order_status === 'delivered';
  const reorderHref = token
    ? `/p/${encodeURIComponent(token)}/reorder`
    : `/q/${encodeURIComponent(shortCode ?? '')}/reorder`;

  if (!isDelivered) {
    return null;
  }

  return (
    <div className="mb-4">
      <InfoBox
        label={t('reorders_label', 'Reorders')}
        icon={<RefreshCw className="h-4 w-4" />}
        testId="patient-reorders-box"
      >
        {!canReorder ? (
          <p className="text-sm text-text-secondary">
            {t(
              'reorder_after_delivery',
              'Reorders open once your pair has been delivered. If you think this is a mistake, contact your clinic.',
            )}
          </p>
        ) : (
          // The same full-width brand pill Link as before (not `<Button asChild>`;
          // this is the money-path CTA we do not want to re-flow).
          <Link
            to={reorderHref}
            data-testid="patient-reorder-cta"
            className={cn(
              'inline-flex w-full h-14 items-center justify-center gap-2',
              'rounded-2xl text-base font-semibold',
              'bg-brand-500 text-text-onBrand hover:bg-brand-600 active:bg-brand-700',
              'shadow-lg shadow-brand-500/25 transition-colors duration-fast',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
            )}
          >
            {t('action_request_reorder', 'Request a new pair')}
            <ArrowRight className="h-5 w-5 rtl-flip" />
          </Link>
        )}
      </InfoBox>
    </div>
  );
}

/**
 * PRESCRIBER: the podiatrist who made them and their clinic. Absorbs the
 * previous "Your clinic" card (clinic name + phone link stay, same testid).
 */
function PrescriberBox({ view, t }: { view: PatientPublicView; t: T }) {
  return (
    <InfoBox
      label={t('prescriber_label', 'Your prescriber')}
      icon={<Stethoscope className="h-4 w-4" />}
      testId="patient-prescriber-box"
    >
      {view.podiatrist_name ? (
        <p className="text-base font-medium text-text-primary" data-testid="patient-prescriber-name">
          {view.podiatrist_name}
        </p>
      ) : null}
      <p
        className={cn('text-sm text-text-primary', view.podiatrist_name ? 'text-text-secondary mt-0.5' : 'font-medium')}
        data-testid="patient-prescriber-clinic"
      >
        {view.clinic_name}
      </p>
      {view.clinic_phone ? (
        <a
          href={`tel:${view.clinic_phone}`}
          className="mt-2 inline-flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700"
          data-testid="patient-clinic-phone"
        >
          <Phone className="h-3.5 w-3.5" />
          <span dir="ltr">{view.clinic_phone}</span>
        </a>
      ) : null}
    </InfoBox>
  );
}

/**
 * PRODUCT INFO: what the product is made of.
 *
 * INSOLES: product family name, the shell material and the top cover
 * material + layer (the layer "None" is not a layer, so it is not printed).
 *
 * SANDALS: the sandal model and its colours (base and upper strap), read
 * from the sandal's own configuration branch. NEVER a shell material or an
 * insole top cover: sandal lines created since 2026-07-26 store the wizard's
 * default insole design branch ("EVA 2mm" / "DryFeet"), which no podiatrist
 * chose, and a sandal must not show it. The server nulls it (migration
 * 20270359); this branch refuses to print it regardless.
 *
 * A line with nothing recorded shows the family name only: nothing is
 * invented.
 */
function ProductBox({ view, t }: { view: PatientPublicView; t: T }) {
  // is_sandal arrives with verify-qr-token v34; product_kind has always been
  // in the payload and covers every sandal stored with its own line type.
  // OR, never ??: the edge coerces an absent is_sandal to a hard `false`, so
  // a new edge over an older view would otherwise switch the fallback off.
  const isSandal = view.is_sandal === true || view.product_kind === 'sandal';

  if (isSandal) {
    const option = (v: string | null | undefined) => (v ? t(`sandal_opt_${v}`, displayValue(v)) : null);
    const model = option(view.sandal_model);
    const base = option(view.sandal_base_color);
    const strap = option(view.sandal_upper_strap_color);
    return (
      <InfoBox
        label={t('product_label_sandal', 'Your sandals')}
        icon={<Package className="h-4 w-4" />}
        testId="patient-product-box"
      >
        <p className="text-base font-medium text-text-primary" data-testid="patient-product-name">
          {/* A legacy sandal stored as custom_insole must not read "Custom insole". */}
          {patientProductName(view.product_kind === 'sandal' ? view.product_name : 'orthopedic_sandal', t)}
        </p>
        {model ? (
          <p className="text-sm text-text-secondary mt-0.5" data-testid="patient-sandal-model">
            {t('sandal_model_line', 'Model: {{model}}', { model })}
          </p>
        ) : null}
        {base ? (
          <p className="text-sm text-text-secondary mt-0.5" data-testid="patient-sandal-base-colour">
            {t('sandal_base_colour_line', 'Base colour: {{colour}}', { colour: base })}
          </p>
        ) : null}
        {strap ? (
          <p className="text-sm text-text-secondary mt-0.5" data-testid="patient-sandal-strap-colour">
            {t('sandal_strap_colour_line', 'Strap colour: {{colour}}', { colour: strap })}
          </p>
        ) : null}
      </InfoBox>
    );
  }

  const cover = [
    view.top_cover_material,
    view.top_cover_layer && view.top_cover_layer !== 'None' ? view.top_cover_layer : null,
  ]
    .filter((v): v is string => !!v)
    .map(displayValue)
    .join(', ');
  return (
    <InfoBox
      label={t('product_label', 'Your insole')}
      icon={<Package className="h-4 w-4" />}
      testId="patient-product-box"
    >
      <p className="text-base font-medium text-text-primary" data-testid="patient-product-name">
        {patientProductName(view.product_name, t)}
      </p>
      {view.material ? (
        <p className="text-sm text-text-secondary mt-0.5" data-testid="patient-product-material">
          {t('reorder_material', 'Material: {{material}}', { material: view.material })}
        </p>
      ) : null}
      {cover ? (
        <p className="text-sm text-text-secondary mt-0.5" data-testid="patient-product-cover">
          {t('product_top_cover', 'Top cover: {{cover}}', { cover })}
        </p>
      ) : null}
    </InfoBox>
  );
}

/**
 * USAGE INSTRUCTIONS. See USAGE_BLOCKS. Full width, last on the page. Kept
 * short on a phone: one compact row per block, small icon, tight leading, no
 * collapsing (instructions must not be hidden behind a tap). From `sm` the
 * eight blocks run in two columns so each line stays about as long as it
 * was in the half-width column; on a phone they are one column, unchanged.
 */
function UsageBox({ t }: { t: T }) {
  return (
    <InfoBox
      label={t('usage_title', 'How to use your INSOLECH product')}
      icon={<BookOpen className="h-4 w-4" />}
      testId="patient-usage-box"
    >
      <ol className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-6" data-testid="patient-usage-blocks">
        {USAGE_BLOCKS.map(({ n, Icon }) => (
          <li key={n} className="flex items-start gap-2" data-testid={`patient-usage-block-${n}`}>
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-text-primary">{t(`usage_${n}_title`)}</p>
              <p className="mt-0.5 text-[13px] leading-snug text-text-secondary">{t(`usage_${n}_body`)}</p>
            </div>
          </li>
        ))}
      </ol>
    </InfoBox>
  );
}

// -------------------- helpers --------------------

/**
 * Stored option values are closed sets. Insole cover values are already
 * human ("EVA 2mm", "Leather"); sandal values are stored as lower-case keys
 * ("military_green"). Underscores become spaces and an all-lower-case value
 * gets a capital; anything else is left alone.
 */
function displayValue(v: string): string {
  const spaced = v.replace(/_/g, ' ');
  return spaced === spaced.toLowerCase() ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : spaced;
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

function defaultStepLabel(key: string): string {
  switch (key) {
    case 'submitted':
      return 'Order placed';
    case 'in_review':
      return 'Reviewed by your clinic';
    case 'in_production':
      return 'Crafting your insoles';
    case 'in_delivery':
      return 'On its way';
    case 'delivered':
      return 'Delivered';
    default:
      return key;
  }
}
